import React, { useState, useEffect, useCallback, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import {
    CarouselWrapper,
    CarouselHeader,
    CarouselSection,
    VideoContainer,
    Video,
    ProjectLink,
    LinkIcon,
    NavigationDots,
    Dot,
    DragHint,
    LoadingOverlay,
    LoadingSpinner,
    SwipeIndicator
} from "./VideoCarousel.styles";
import { Heading } from "../_Common/common.styles";

// Project data - easily add your project links here!
const projects = [
    {
        id: 1,
        title: "Site 1",
        video: "site1-1762984933392",
        link: "https://siteexample01.netlify.app/"
    },
    {
        id: 2,
        title: "Site 2",
        video: "site2-1762984605420",
        link: "https://siteexample02.netlify.app/"
    },
    {
        id: 3,
        title: "Site 3",
        video: "site3-1762982566097",
        link: "https://siteexample03.netlify.app/"
    },
    {
        id: 4,
        title: "Site 4",
        video: "site4-1762982142278",
        link: "https://siteexample04.netlify.app/"
    },
    {
        id: 5,
        title: "Site 5",
        video: "site5-1762981421277",
        link: "https://siteexample05.netlify.app/"
    },
    {
        id: 6,
        title: "Site 6",
        video: "site6-1762980451128",
        link: "https://siteexample06.netlify.app/"
    },
    {
        id: 7,
        title: "Site 7",
        video: "site7-1762978914201",
        link: "https://siteexample07.netlify.app/"
    },
    {
        id: 8,
        title: "Site 8",
        video: "site8-1762977994761",
        link: "https://siteexample08.netlify.app/"
    },
    {
        id: 9,
        title: "Site 9",
        video: "site9-1762977249549",
        link: "https://siteexample09.netlify.app/"
    },
    {
        id: 10,
        title: "Site 10",
        video: "site10-1762976516781",
        link: "https://siteexample10.netlify.app/"
    },
    {
        id: 11,
        title: "GLAD",
        video: "siteGLAD-1762985720862",
        link: "https://groupeleclerc.net/"
    },
    {
        id: 12,
        title: "OnTheWeb",
        video: "siteOntheweb-1762986019171",
        link: "https://ontheweb.agency/"
    }
];

// Posters are generated from frame 1 of each video (see scripts/generate-posters.sh).
// They let the browser paint the slide without downloading any video bytes.
const posterFor = (video) => `/posters/${video}.webp`;

// HEVC first for browsers that can decode it (roughly half the bytes of the AVC
// build), H.264 as the universal fallback. Encoded by scripts/encode-videos.sh.
const sourcesFor = (video) => [
    { src: `/videos/${video}-h265.mp4`, type: 'video/mp4; codecs="hvc1"' },
    { src: `/videos/${video}-h264.mp4`, type: "video/mp4" }
];

// Wraps the <video> so that a slide leaving the crossfade aborts its download.
// AnimatePresence keeps the outgoing node alive for the 0.6s transition; without
// an explicit teardown that node keeps streaming its file alongside the incoming
// one, which is where the overlapping duplicate requests came from.
const CarouselVideo = React.forwardRef(({ project, onLoaded, onError }, ref) => {
    const localRef = useRef(null);

    const setRefs = useCallback((node) => {
        localRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
    }, [ref]);

    useEffect(() => () => {
        const video = localRef.current;
        if (!video) return;
        video.pause();
        // Sources have to be detached as well as src: load() only cancels the
        // in-flight request once there is nothing left for it to resolve.
        video.removeAttribute("src");
        while (video.firstChild) video.removeChild(video.firstChild);
        video.load();
    }, []);

    return (
        <Video
            ref={setRefs}
            poster={posterFor(project.video)}
            preload="none"
            autoPlay
            loop
            muted
            playsInline
            onLoadedData={onLoaded}
            onError={onError}
        >
            {sourcesFor(project.video).map(source => (
                <source key={source.src} src={source.src} type={source.type} />
            ))}
        </Video>
    );
});

const VideoCarousel = () => {
    const { t } = useTranslation();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [showHint, setShowHint] = useState(true);
    const [swipeDirection, setSwipeDirection] = useState(null); // 'left' or 'right'
    const [swipeProgress, setSwipeProgress] = useState(0); // 0 to 1
    // Nothing about the carousel runs until it is actually on screen: no video
    // element is mounted, no bytes are fetched, and the rotation timer is idle.
    const { ref: inViewRef, inView } = useInView({
        rootMargin: "200px 0px",
        threshold: 0
    });
    const videoRef = useRef(null);
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);

    const autoPlayDelay = 8000; // 8 seconds per video

    // AnimatePresence keys must never repeat. currentIndex wraps 0..11..0, so a
    // returning slide could collide with a node still exiting under the same key,
    // which left AnimatePresence unable to unmount either one - videos piled up
    // in the DOM, each holding its own download. A monotonic counter keeps every
    // key unique for the life of the page.
    const slideKeyRef = useRef(0);
    const prevIndexRef = useRef(currentIndex);
    if (prevIndexRef.current !== currentIndex) {
        prevIndexRef.current = currentIndex;
        slideKeyRef.current += 1;
    }
    const slideKey = slideKeyRef.current;

    // Auto-play logic
    const nextSlide = useCallback(() => {
        setDirection(1);
        setCurrentIndex(prevIndex =>
            prevIndex === projects.length - 1 ? 0 : prevIndex + 1
        );
    }, []);

    const previousSlide = useCallback(() => {
        setDirection(-1);
        setCurrentIndex(prevIndex =>
            prevIndex === 0 ? projects.length - 1 : prevIndex - 1
        );
    }, []);

    // Autoplay timer
    useEffect(() => {
        let intervalId;

        if (isAutoPlaying && inView) {
            intervalId = setInterval(nextSlide, autoPlayDelay);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isAutoPlaying, inView, nextSlide]);

    // Hide drag hint after a few seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowHint(false);
        }, 5000);
        return () => clearTimeout(timer);
    }, []);

    // Handle video loaded
    const handleVideoLoaded = () => {
        setIsLoading(false);
        if (videoRef.current) {
            videoRef.current.play().catch(err => {
                console.log("Autoplay prevented:", err);
            });
        }
    };

    // Handle dot click
    const handleDotClick = (index) => {
        setDirection(index > currentIndex ? 1 : -1);
        setCurrentIndex(index);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 15000);
    };

    // Pause on hover
    const handleMouseEnter = () => {
        setIsAutoPlaying(false);
    };

    const handleMouseLeave = () => {
        setIsAutoPlaying(true);
        // Reset swipe indicators if leaving while dragging
        if (touchStartX.current !== 0) {
            touchStartX.current = 0;
            touchEndX.current = 0;
            setSwipeDirection(null);
            setSwipeProgress(0);
        }
    };

    // Swipe handlers - no visual drag, just gesture detection
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        setIsAutoPlaying(false);
        setShowHint(false);
    };

    const handleTouchMove = (e) => {
        touchEndX.current = e.touches[0].clientX;
        const distance = touchStartX.current - touchEndX.current;
        const maxDistance = 150; // Max distance for full glow effect

        // Calculate progress (0 to 1)
        const progress = Math.min(Math.abs(distance) / maxDistance, 1);
        setSwipeProgress(progress);

        // Set direction
        if (distance > 10) {
            setSwipeDirection('left'); // Swiping left (next)
        } else if (distance < -10) {
            setSwipeDirection('right'); // Swiping right (previous)
        }
    };

    const handleTouchEnd = () => {
        const swipeDistance = touchStartX.current - touchEndX.current;
        const swipeThreshold = 50;

        if (Math.abs(swipeDistance) > swipeThreshold) {
            if (swipeDistance > 0) {
                nextSlide();
            } else {
                previousSlide();
            }
        }

        // Reset swipe indicators
        setSwipeDirection(null);
        setSwipeProgress(0);
        setTimeout(() => setIsAutoPlaying(true), 15000);
    };

    // Mouse drag handlers for desktop
    const handleMouseDown = (e) => {
        touchStartX.current = e.clientX;
        setIsAutoPlaying(false);
        setShowHint(false);
    };

    const handleMouseMove = (e) => {
        if (touchStartX.current !== 0) {
            touchEndX.current = e.clientX;
            const distance = touchStartX.current - touchEndX.current;
            const maxDistance = 150;

            // Calculate progress (0 to 1)
            const progress = Math.min(Math.abs(distance) / maxDistance, 1);
            setSwipeProgress(progress);

            // Set direction
            if (distance > 10) {
                setSwipeDirection('left');
            } else if (distance < -10) {
                setSwipeDirection('right');
            }
        }
    };

    const handleMouseUp = () => {
        if (touchStartX.current !== 0) {
            const swipeDistance = touchStartX.current - touchEndX.current;
            const swipeThreshold = 50;

            if (Math.abs(swipeDistance) > swipeThreshold) {
                if (swipeDistance > 0) {
                    nextSlide();
                } else {
                    previousSlide();
                }
            }

            touchStartX.current = 0;
            touchEndX.current = 0;

            // Reset swipe indicators
            setSwipeDirection(null);
            setSwipeProgress(0);
            setTimeout(() => setIsAutoPlaying(true), 15000);
        }
    };

    const currentProject = projects[currentIndex];
    const hasLink = currentProject.link && currentProject.link !== "";

    // Animation variants - smooth crossfade
    const videoVariants = {
        enter: {
            opacity: 0,
            scale: 1.05
        },
        center: {
            opacity: 1,
            scale: 1
        },
        exit: {
            opacity: 0,
            scale: 0.95
        }
    };

    const linkVariants = {
        initial: { opacity: 0, scale: 0.8 },
        animate: {
            opacity: 1,
            scale: 1,
            transition: {
                delay: 0.5,
                duration: 0.4,
                ease: "easeOut"
            }
        },
        exit: {
            opacity: 0,
            scale: 0.8,
            transition: { duration: 0.2 }
        }
    };

    const hintVariants = {
        initial: { opacity: 0 },
        animate: {
            opacity: 1,
            transition: {
                delay: 2,
                duration: 1
            }
        },
        exit: {
            opacity: 0,
            transition: { duration: 0.5 }
        }
    };

    return (
        <CarouselWrapper id="projects">
            <CarouselHeader>
                <Heading>{t("mainText.portfolio")}</Heading>
            </CarouselHeader>

            <CarouselSection
                ref={inViewRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
            {/* Swipe Feedback Indicators */}
            {swipeDirection && (
                <SwipeIndicator
                    $direction={swipeDirection}
                    $progress={swipeProgress}
                />
            )}

            {/* Main video carousel.
                Deliberately not wrapped in AnimatePresence. Its exit animations
                completed but it never released the children, so every slide left
                a <video> behind - each one holding a finished download and, once
                the index wrapped, painting a ghost over the live slide. mode="wait"
                deadlocked instead of leaking. Letting React unmount the old slide
                on key change is deterministic: exactly one video exists at a time
                and CarouselVideo's cleanup runs immediately. The incoming slide
                keeps its fade+scale entrance. */}
                <VideoContainer
                    key={slideKey}
                    variants={videoVariants}
                    initial="enter"
                    animate="center"
                    transition={{
                        duration: 0.6,
                        ease: "easeInOut"
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{ cursor: 'grab' }}
                >
                    {inView ? (
                        <CarouselVideo
                            ref={videoRef}
                            project={currentProject}
                            onLoaded={handleVideoLoaded}
                            onError={() => setIsLoading(false)}
                        />
                    ) : (
                        // Same styled element rendered as an <img>, so the slide looks
                        // identical off-screen without holding a media element open.
                        <Video
                            as="img"
                            src={posterFor(currentProject.video)}
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                    )}

                    {/* Project Link Button */}
                    {hasLink && (
                        <ProjectLink
                            href={currentProject.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            variants={linkVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            onClick={(e) => e.stopPropagation()}
                            title="Visit Project"
                        >
                            <LinkIcon viewBox="0 0 24 24">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                            </LinkIcon>
                        </ProjectLink>
                    )}
                </VideoContainer>

            {/* Loading Overlay */}
            <AnimatePresence>
                {isLoading && inView && (
                    <LoadingOverlay
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <LoadingSpinner
                            animate={{ rotate: 360 }}
                            transition={{
                                duration: 1,
                                repeat: Infinity,
                                ease: "linear"
                            }}
                        />
                    </LoadingOverlay>
                )}
            </AnimatePresence>

            {/* Swipe Hint */}
            <AnimatePresence>
                {showHint && !isLoading && (
                    <DragHint
                        variants={hintVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                    >
                        <span>← Swipe to navigate →</span>
                    </DragHint>
                )}
            </AnimatePresence>

            {/* Navigation Dots */}
            <NavigationDots>
                {projects.map((_, index) => (
                    <Dot
                        key={index}
                        $active={index === currentIndex}
                        onClick={() => handleDotClick(index)}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                        aria-label={`Go to project ${index + 1}`}
                        title={`Project ${index + 1}`}
                    />
                ))}
            </NavigationDots>
            </CarouselSection>
        </CarouselWrapper>
    );
};

export default VideoCarousel;
